import dayjs from 'dayjs'
import { forEach, map, reduce, union } from 'lodash'
import { useMemo } from 'react'

import { AvailabilityMeetingPlace, GetMeetingLinkQuery } from '@/core/graphql/types'

export type Schedule = {
  address: { description: string; placeId: string } | null
  busySlots: Array<{ start: string; end: string }>
  duration: number
  isRecurrence: boolean
  meetingPlace: AvailabilityMeetingPlace
  recurrence?: Array<{
    days: number[]
    startTime: number
    endTime: number
  }>
  scheduledDay?: Date
  scheduledPeriods?: Array<{ startTime: number; endTime: number }>
  specialities: string[]
  unavailabilities?: Array<{ startDate: string; endDate: string; startTime?: number | null; endTime?: number | null }>
}

export const useSchedules = (meetingLink?: GetMeetingLinkQuery['meetingLink']) => {
  const schedules = useMemo(() => {
    if (!meetingLink) return []

    const { availabilities, busySlots } = meetingLink

    return reduce(
      availabilities,
      (schedules: Schedule[], availability) => {
        const unavailabilities = availability.unavailabilities?.map((u) => ({
          startDate: u.startDate,
          endDate: u.endDate,
          startTime: u.startTime,
          endTime: u.endTime,
        }))

        if (availability.isRecurrence) {
          schedules.push({
            address: availability.address || null,
            busySlots,
            duration: availability.avgMeetingDuration,
            isRecurrence: true,
            meetingPlace: availability.meetingPlace,
            unavailabilities,
            recurrence: reduce(
              availability.recurrenceDays,
              (recurrenceResult, recurrenceDay) => {
                if (!recurrenceResult) return []

                const periods = map(recurrenceDay.periods, (period) => ({
                  days: [recurrenceDay.day],
                  startTime: period.open,
                  endTime: period.close,
                }))

                if (!recurrenceResult.length) recurrenceResult.push(...periods)

                forEach(recurrenceResult, (item) => {
                  for (const period of periods) {
                    if (period.startTime === item.startTime && period.endTime === item.endTime) {
                      item.days = union(item.days, period.days)
                    } else {
                      recurrenceResult.push(period)
                    }
                  }
                })

                return recurrenceResult
              },
              [] as Schedule['recurrence']
            ),
            specialities: availability.specialities,
          })
        }
        schedules = schedules.concat(
          map(availability.scheduledDays, ({ day, periods }) => ({
            address: availability.address || null,
            busySlots,
            duration: availability.avgMeetingDuration,
            isRecurrence: false,
            meetingPlace: availability.meetingPlace,
            scheduledDay: dayjs(day, 'DD/MM/YYYY').toDate(),
            scheduledPeriods: map(periods, (period) => ({
              startTime: period.open,
              endTime: period.close,
            })),
            specialities: availability.specialities,
            unavailabilities,
          }))
        )
        return schedules
      },
      []
    )
  }, [meetingLink])

  return schedules
}
